import {
  ArgsType, Directive, Field, ID, InputType, ObjectType,
} from '@nestjs/graphql';
import {
  IsBoolean, IsIn, IsOptional, IsString,
  ValidateIf,
  ValidateNested,
} from 'class-validator';
import { GraphQLJSONObject } from 'graphql-type-json';
import { Type } from 'class-transformer';
import {
  reportTypes, reportPseudoStatuses, reportMetadataKeys,
  reportSortColumns, reportAuditActions, reportStatuses,
} from './Reports.constants';
import { Approval } from '../approvals/Approvals.types';
import { PaginationDTO } from '../common/Pagination.types';
import { ToBoolean } from '../utils/ToBoolean.util';

export type ReportType = typeof reportTypes[number];
export type ReportPseudoStatus = typeof reportPseudoStatuses[number];
export type ReportMetadataKey = typeof reportMetadataKeys[number];
export type ReportSortColumns = typeof reportSortColumns[number];
export type ReportAuditAction = typeof reportAuditActions[number];
export type ReportStatus = typeof reportStatuses[number];
export type ReportMetadata = Partial<Record<ReportMetadataKey, string>>;

@ObjectType()
@Directive('@key(fields: "id")')
export class Report {
  @Field(() => ID, { nullable: false })
    id: string;

  @Field(() => String, { nullable: true })
    documentId: string | null;

  @Field(() => String, { nullable: false })
    analysisSetId: string;

  @Field(() => String, { nullable: false })
    type: ReportType;

  @Field(() => String, { nullable: false })
    status: ReportStatus;

  @Field(() => String, { nullable: true })
    pseudoStatus: ReportPseudoStatus | null;

  @Field(() => String, { nullable: true })
    approvedAt: string | null;

  @Field(() => String, { nullable: true })
    fileId?: string | null;

  @Field(() => [Approval], { nullable: true })
    approvals?: Approval[];

  @Field(() => GraphQLJSONObject, { nullable: true })
    metadata?: ReportMetadata;

  @Field(() => String, { nullable: false })
    createdAt: string;

  @Field(() => String, { nullable: false })
    createdBy: string;

  @Field(() => String, { nullable: true })
    updatedAt: string | null;

  @Field(() => String, { nullable: true })
    updatedBy: string | null;

  @Field(() => String, { nullable: true })
    amendmentOf: string | null;
}

@ArgsType()
export class ReportFiltersDTO extends PaginationDTO {
  @IsOptional()
  @IsString({ each: true })
  @Field(() => [String], { nullable: true })
    analysisSetIds?: string[];

  @IsOptional()
  @IsIn(reportTypes, { each: true })
  @Field(() => [String], { nullable: true })
    types?: ReportType[];

  @IsOptional()
  @IsIn(reportStatuses, { each: true })
  @Field(() => [String], { nullable: true })
    statuses?: ReportStatus[];

  @IsOptional()
  @IsIn(reportPseudoStatuses, { each: true })
  @Field(() => [String], { nullable: true })
    pseudoStatuses?: ReportPseudoStatus[];

  @IsOptional()
  @IsString()
  @Field(() => String, { nullable: true })
    fileId?: string;

  @IsOptional()
  @IsString({ each: true })
  @Field(() => [String], { nullable: true })
    approvers?: string[];

  @IsOptional()
  @IsBoolean()
  @ToBoolean()
    includeApprovals?: boolean;

  @IsOptional()
  @IsIn(reportSortColumns, { each: true })
  @Field(() => [String], { nullable: true })
    sortColumns?: ReportSortColumns[];

  @IsOptional()
  @IsIn(['asc', 'desc'], { each: true })
  @Field(() => [String], { nullable: true })
    sortDirections?: ('asc' | 'desc')[];
}

@InputType()
export class CreateReportsBodyDTO {
  @IsString()
  @Field(() => String, { nullable: false })
    analysisSetId: string;

  @IsIn(reportTypes)
  @Field(() => String, { nullable: false })
    type: ReportType;

  @IsIn(reportStatuses)
  @Field(() => String, { nullable: false })
    status: ReportStatus;

  @IsOptional()
  @IsString()
  @Field(() => String, { nullable: true })
    fileId?: string;

  @IsOptional()
  @IsString()
  @Field(() => String, { nullable: true })
    amendmentOf?: string;
}

@InputType()
export class UpdateReportsBodyDTO {
  @IsOptional()
  @IsIn(reportStatuses)
  @Field(() => String, { nullable: true })
    status?: ReportStatus;

  @IsOptional()
  @IsIn(reportPseudoStatuses)
  @ValidateIf((o, v) => v !== null)
  @Field(() => String, { nullable: true })
    pseudoStatus?: ReportPseudoStatus | null;

  @IsOptional()
  @IsString()
  @Field(() => String, { nullable: true })
    fileId?: string;

  @IsOptional()
  @IsString()
  @Field(() => String, { nullable: true })
    replaceComment?: string;
}

@InputType()
export class ResendReportBodyDTO {
  @IsString()
  @Field(() => String, { nullable: false })
    reason: string;
}

/**
 * A report plus the patient context the cron jobs need, read without user access
 * control. Patient fields are nullable because the joins are left joins — a report
 * whose analysis set or patient row is missing must still surface (and fail loudly)
 * rather than silently vanish from the queue.
 */
export interface ISystemReport {
  id: string;
  type: ReportType;
  fileId: string | null;
  approvedAt: string | null;
  analysisSetId: string;
  patientId: string | null;
  publicSubjectId: string | null;
  lmSubjectId: string | null;
  hospital: string | null;
  cohort: string | null;
  c1EventNum: number | null;
  amendmentOf: string | null;
}

export interface IReportSite {
  siteCode: string;
  displayName: string;
  /** Normalised free-text spellings that resolve to this site. */
  aliases: string[];
}

export interface IReportRecipient {
  /** Null for core recipients, who receive this report type for every site. */
  siteCode: string | null;
  reportType: ReportType;
  email: string;
  displayName: string | null;
  sourceRow: number | null;
}

/**
 * Outcome of resolving who a report should be emailed to. `errors` being non-empty
 * means the report must not be sent — recipients are never partially guessed.
 */
export interface IResolvedRecipients {
  /** The patient's own clinicians — the treating oncologist and the pathologist. */
  to: string[];
  /**
   * Names of those clinicians as Clinical One holds them, for the salutation.
   * Aligned with `to`, minus any the record has no name for.
   */
  toNames: string[];
  /** The configured distribution list for this report type and site. */
  cc: string[];
  /** `to` followed by `cc`, deduplicated — every address the report goes to. */
  emails: string[];
  siteCode: string | null;
  errors: string[];
}

export interface IReportAuditLog {
  id: string;
  reportId: string;
  actionType: ReportAuditAction;
  userId: string;
  createdAt: string;
  comment: string | null;
  oldFileId: string | null;
  newFileId: string | null;
}

@InputType()
export class UpdateReportMetadataKey {
  @IsIn(reportMetadataKeys)
  @Field(() => String, { nullable: false })
    key: ReportMetadataKey;

  @IsString()
  @Field(() => String, { nullable: false })
    value: string;
}

@InputType()
export class UpdateReportMetadataDTO {
  @ValidateNested({ each: true })
  @Type(() => UpdateReportMetadataKey)
  @Field(() => [UpdateReportMetadataKey])
    metadata: UpdateReportMetadataKey[];
}
